import { Link, usePage } from '@inertiajs/react'
import { type ReactNode, useEffect, useState } from 'react'
import { useI18n } from '../../lib/useI18n'
import LocaleSwitcher from '../LocaleSwitcher'
import NotificationBell from '../NotificationBell'
import ThemeToggle from '../ThemeToggle'
import BrandLogo from './BrandLogo'
import CommandPalette, { type Command } from './CommandPalette'
import Icon, { type IconName } from './Icon'
import Tooltip from './Tooltip'
import UserMenu from './UserMenu'

/**
 * Shared FreightOS shell for the authenticated surfaces (operator + portal),
 * built 1:1 to mockups/{operator,portal} `buildShell`: navy sidebar (logo +
 * subtitle, nav sections, footer) + a top bar (collapse toggle, breadcrumb,
 * optional global search, locale, theme, notifications, user).
 *
 * Responsive: a drawer on phones (< md); a static sidebar on tablet/desktop that
 * collapses to a 72px icon-rail via the top-bar toggle. The collapse choice is
 * persisted to `localStorage` (`g4-side`); with no saved choice the sidebar
 * auto-collapses on tablet widths (md–lg) and expands on large screens.
 *
 * Operator/Portal pass their own `nav`, `subtitle`, `footer`, and (portal)
 * `banners` + `sidebarTop` (company switcher). Marketing has its own shell.
 */

export type ShellNavItem = {
  href: string
  label: string
  icon: IconName
  badge?: number
  /** Feature flag key; the item is hidden when that flag is disabled (§6.20). */
  feature?: string
  /** Nested sub-items, revealed (indented) when the parent or a child is active. */
  children?: ShellNavItem[]
}

// Drop nav items whose feature flag is off (mandatory/unknown keys stay visible),
// recursing into children and pruning groups left empty. Keeps the shells'
// navigation in lockstep with server-side feature gates (§6.20).
function visibleNav(groups: ShellNavGroup[], features: Record<string, boolean>): ShellNavGroup[] {
  const keep = (item: ShellNavItem) => !item.feature || features[item.feature] !== false
  const filterItems = (items: ShellNavItem[]): ShellNavItem[] =>
    items.filter(keep).map((i) => (i.children ? { ...i, children: filterItems(i.children) } : i))
  return groups
    .map((g) => ({ ...g, items: filterItems(g.items) }))
    .filter((g) => g.items.length > 0)
}
export type ShellNavGroup = { section?: string; items: ShellNavItem[] }

type Props = {
  subtitle: string
  nav: ShellNavGroup[]
  footer?: ReactNode
  children: ReactNode
  /** Optional global-search affordance in the top bar. */
  search?: { href: string; placeholder: string }
  /** Slot under the brand in the sidebar (e.g. the portal company switcher). */
  sidebarTop?: ReactNode
  /** Full-width strip above the shell (e.g. impersonation / announcements). */
  banners?: ReactNode
  /** Mobile-first bottom tab bar (portal): primary destinations, phone only. */
  bottomNav?: ShellNavItem[]
  /** Hide the top-bar breadcrumb entirely. It only ever resolves to a single
   *  section label (no hierarchy) and duplicates each page's own header, so
   *  both consoles (operator + portal) drop it. */
  hideCrumb?: boolean
  /** Top-bar search placement. Default 'right'; both consoles use 'center' so
   *  the search sits between the sidebar toggle and the right-hand controls
   *  (the crumb is hidden, freeing that space). */
  searchAlign?: 'center' | 'right'
}

const TABLET = '(min-width: 768px) and (max-width: 1023px)'

// True on tablet widths — guarded for SSR + jsdom (no matchMedia).
function isTabletWidth(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(TABLET).matches
}

function isActive(href: string, url: string): boolean {
  if (href === '/') return url === '/'
  return url === href || url.startsWith(`${href}/`)
}

function crumbFor(nav: ShellNavGroup[], url: string): string {
  for (const group of nav) {
    for (const item of group.items) if (isActive(item.href, url)) return item.label
  }
  const seg = url.split('?')[0].split('/').filter(Boolean)[0]
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : 'Dashboard'
}

function NavLink({
  item,
  active,
  collapsed,
}: {
  item: ShellNavItem
  active: boolean
  collapsed: boolean
}) {
  return (
    <Link
      href={item.href}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      className={`group relative flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-colors ${
        collapsed ? 'md:mx-auto md:h-11 md:w-11 md:justify-center md:gap-0 md:p-0' : ''
      } ${
        active
          ? 'bg-[var(--nav-bg-2)] font-semibold text-white'
          : 'text-[var(--nav-ink)] hover:bg-[var(--nav-bg-2)] hover:text-white'
      }`}
    >
      {active && (
        <span
          className="absolute -left-3.5 top-2 bottom-2 w-0.5 rounded-r bg-[var(--nav-trim)]"
          aria-hidden
        />
      )}
      <Icon name={item.icon} size={collapsed ? 20 : 18} className="flex-none" />
      <span className={`flex-1 ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
      {item.badge ? (
        <span
          className={`rounded-full bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold text-accent-ink ${
            collapsed ? 'md:hidden' : ''
          }`}
        >
          {item.badge}
        </span>
      ) : null}
    </Link>
  )
}

export default function AppShell({
  subtitle,
  nav: rawNav,
  footer,
  children,
  search,
  sidebarTop,
  banners,
  bottomNav: rawBottomNav,
  hideCrumb = false,
  searchAlign = 'right',
}: Props) {
  const { props, url } = usePage()
  const { t } = useI18n()
  const features = (props.features as Record<string, boolean> | undefined) ?? {}
  const nav = visibleNav(rawNav, features)
  const bottomNav = rawBottomNav
    ? visibleNav([{ items: rawBottomNav }], features)[0]?.items
    : undefined
  const unread = (props.notifications_unread as number | undefined) ?? 0
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    const saved = localStorage.getItem('g4-side')
    if (saved === 'collapsed') return true
    if (saved === 'open') return false
    return isTabletWidth() // auto-collapse on tablet
  })
  const crumb = crumbFor(nav, url)

  // ⌘K command palette (only when the surface enables search).
  const [paletteOpen, setPaletteOpen] = useState(false)
  useEffect(() => {
    if (!search) return
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [search])
  const commands: Command[] = nav.flatMap((g) =>
    g.items.map((i) => ({
      label: t('app.cmdk.goto', { label: i.label }),
      icon: i.icon,
      href: i.href,
    })),
  )

  // Re-apply the auto behaviour on resize, unless the user made an explicit choice.
  useEffect(() => {
    if (typeof window === 'undefined') return
    function onResize() {
      if (localStorage.getItem('g4-side')) return
      setCollapsed(isTabletWidth())
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // One toggle for both modes: drawer on mobile, collapse (persisted) on >= md.
  function toggleSidebar() {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setDrawerOpen((o) => !o)
      return
    }
    setCollapsed((c) => {
      const next = !c
      if (typeof window !== 'undefined') {
        localStorage.setItem('g4-side', next ? 'collapsed' : 'open')
      }
      return next
    })
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-bg font-sans text-ink">
      {banners}
      <div className="flex min-h-0 flex-1">
        {drawerOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-30 bg-black/45 md:hidden"
          />
        )}

        <aside
          className={`fixed inset-y-0 left-0 z-40 flex w-[250px] flex-col overflow-y-auto overflow-x-hidden bg-[var(--nav-bg)] px-3.5 py-4 transition-all md:static md:translate-x-0 ${
            collapsed ? 'md:w-[72px]' : 'md:w-[248px]'
          } ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          <div
            className={`flex items-center gap-3 px-2 pb-4 pt-1.5 ${
              collapsed ? 'md:justify-center md:gap-0 md:px-0' : ''
            }`}
          >
            <BrandLogo tone="dark" className="h-9 w-9 flex-none" />
            <div className={collapsed ? 'md:hidden' : ''}>
              <div className="font-display text-[15px] font-extrabold tracking-tight text-white">
                Geo4Logistics
              </div>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--nav-faint)]">
                {subtitle}
              </div>
            </div>
          </div>

          {sidebarTop ? <div className={collapsed ? 'md:hidden' : 'mb-1'}>{sidebarTop}</div> : null}

          <nav className="flex flex-col gap-0.5">
            {nav.map((group, gi) => (
              <div key={group.section ?? `group-${gi}`}>
                {group.section ? (
                  <div
                    className={`px-2.5 pb-2 pt-3.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--nav-faint)] ${
                      collapsed ? 'md:hidden' : ''
                    }`}
                  >
                    {group.section}
                  </div>
                ) : null}
                {group.items.map((item) => {
                  const parentActive = isActive(item.href, url)
                  const childActive = item.children?.some((c) => isActive(c.href, url)) ?? false
                  const link = <NavLink item={item} active={parentActive} collapsed={collapsed} />
                  return (
                    <div key={item.href}>
                      {collapsed ? (
                        // Collapsed rail: reveal the label as a styled flyout to
                        // the right (the rail clips overflow, so this portals out).
                        <Tooltip
                          label={item.label}
                          side="right"
                          wrapperClassName="block"
                          className="whitespace-nowrap text-sm font-medium"
                        >
                          {link}
                        </Tooltip>
                      ) : (
                        link
                      )}
                      {item.children && (parentActive || childActive) && !collapsed ? (
                        <div className="ml-[22px] flex flex-col gap-0.5 border-l border-[var(--nav-bg-2)] pl-2">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.href}
                              item={child}
                              active={isActive(child.href, url)}
                              collapsed={collapsed}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ))}
          </nav>

          {footer ? (
            <div
              className={`mt-auto px-2 pt-3 text-[11px] leading-snug text-[var(--nav-faint)] ${
                collapsed ? 'md:hidden' : ''
              }`}
            >
              {footer}
            </div>
          ) : null}
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex h-[62px] flex-none items-center gap-3 border-b border-border bg-surface px-4 md:px-6">
            <button
              type="button"
              aria-label="Toggle sidebar"
              onClick={toggleSidebar}
              className="grid h-9 w-9 flex-none place-items-center rounded-[10px] border border-transparent text-ink-2 hover:border-border hover:bg-surface-3 hover:text-ink"
            >
              <Icon name="menu" size={18} />
            </button>

            {!hideCrumb && (
              <div className="flex min-w-0 items-center gap-2 text-sm text-ink-muted">
                <span className="hidden sm:inline">Geo4Logistics</span>
                <Icon name="chevron-right" size={14} className="hidden flex-none sm:inline-block" />
                <b className="truncate font-semibold text-ink">{crumb}</b>
              </div>
            )}

            {search ? (
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className={`hidden min-w-[260px] items-center gap-2 rounded-[11px] border border-border bg-surface-2 px-3 py-2 text-left text-sm text-ink-muted hover:border-accent md:flex ${
                  searchAlign === 'right' ? 'ml-auto' : 'mx-auto md:min-w-[380px] md:max-w-xl'
                }`}
              >
                <Icon name="search" size={15} />
                <span className="flex-1">{search.placeholder}</span>
                <kbd className="rounded-md border border-border px-1.5 font-mono text-[10px]">
                  ⌘K
                </kbd>
              </button>
            ) : null}

            <div className={`flex items-center gap-2 ${search ? 'ml-auto md:ml-0' : 'ml-auto'}`}>
              <LocaleSwitcher />
              <ThemeToggle />
              <NotificationBell href="/notifications" initialUnread={unread} />
              <UserMenu />
            </div>
          </header>

          {/* `scroll-region` is how Inertia is told this — not the window — is what
              scrolls. Without it, opening a shipment from a scrolled list left the
              detail page sitting at the previous scroll offset: Inertia reset
              `window.scrollTo(0, 0)`, but the window was never scrolled, and this
              element kept its own scrollTop across the page swap. With it, a fresh
              visit starts at the top and going back restores where you were. */}
          <main
            scroll-region=""
            className={`flex-1 overflow-y-auto px-4 py-6 md:px-7 md:py-7 ${bottomNav ? 'pb-24 md:pb-7' : ''}`}
          >
            {children}
          </main>
        </div>
      </div>

      {bottomNav ? (
        <nav
          aria-label="Primary"
          className="fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
        >
          {bottomNav.map((item) => {
            const active = isActive(item.href, url)
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-semibold ${
                  active ? 'text-accent' : 'text-ink-muted'
                }`}
              >
                <span className="relative">
                  <Icon name={item.icon} size={20} />
                  {item.badge ? (
                    <span className="-right-2 -top-1 absolute rounded-full bg-accent px-1 font-mono text-[9px] text-accent-ink">
                      {item.badge}
                    </span>
                  ) : null}
                </span>
                {item.label}
              </Link>
            )
          })}
        </nav>
      ) : null}

      {search ? (
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          commands={commands}
          searchPath={search.href}
          storageKey={`g4:recent-search:${(props.current_user as { id?: string } | null)?.id ?? 'anon'}`}
        />
      ) : null}
    </div>
  )
}
